import React, { useMemo } from 'react';
import { components } from 'react-select';
import { Box, Flex } from '@chakra-ui/react';
import { KText } from '@/ui-library/components';
import { CheckCircle } from '@kula-ai/icons-core';

interface CandidateSource {
  id: number;
  name: string;
  parent_id?: number | null;
  enabled?: boolean;
  deleted?: boolean;
}

/**
 * Check if an item has children
 */
const hasChildren = (item: CandidateSource, allItems: CandidateSource[]) => {
  return allItems.some((i) => i.parent_id === item.id);
};

/**
 * Custom SingleValue component to display selected source name
 * Uses KSelect singleValue styles: fontWeight 450, color secondary-700
 */
export const CandidateSourceSingleValue = (props: any) => {
  const { data } = props;

  return (
    <components.SingleValue {...props}>
      <KText color="secondary-700" fontSize="14px" fontWeight={450}>
        {data?.name}
      </KText>
    </components.SingleValue>
  );
};

// Search result item component for candidate source
const CandidateSourceSearchResultItem = ({
  item,
  items,
  onSelect,
  onClose,
  isSelected,
}: {
  item: CandidateSource;
  items: CandidateSource[];
  onSelect: (item: CandidateSource) => void;
  onClose?: () => void;
  isSelected: boolean;
}) => {
  const isParent = hasChildren(item, items);

  const handleClick = () => {
    if (isParent) return;
    onSelect(item);
    onClose?.();
  };

  return (
    <Box
      p="12px"
      display="flex"
      cursor={isParent ? 'not-allowed' : 'pointer'}
      _hover={{ bgColor: !isParent ? 'secondary-25' : undefined }}
      onClick={handleClick}
      bgColor={isSelected ? 'secondary-25' : 'transparent'}
      opacity={isParent ? 0.5 : 1}
      borderBottom="1px solid"
      borderColor="border-color"
      alignItems="center"
      justifyContent="space-between"
    >
      <KText
        fontSize="14px"
        color={isSelected ? 'primary-600' : 'secondary-700'}
        fontWeight={450}
        isTruncated
      >
        {item.name}
      </KText>
      {isSelected && (
        <CheckCircle
          size={16}
          color="var(--kula-colors-primary-500)"
          className="kula-icon"
          style={{ flexShrink: 0 }}
        />
      )}
    </Box>
  );
};

// Tree item component for candidate source
const CandidateSourceTreeItem = ({
  item,
  items,
  onSelect,
  onClose,
  currentSelectedValue,
}: {
  item: CandidateSource;
  items: CandidateSource[];
  onSelect: (item: CandidateSource) => void;
  onClose?: () => void;
  currentSelectedValue?: CandidateSource | null;
}) => {
  const children = items?.filter((child) => child?.parent_id === item?.id);
  const isSelected = currentSelectedValue?.id === item?.id;
  const isParent = children.length > 0;

  const handleSelect = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (isParent) return;
    onSelect(item);
    onClose?.();
  };

  return (
    <>
      <Box
        data-item-id={item.id}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        p="12px"
        cursor={isParent ? 'not-allowed' : 'pointer'}
        _hover={{ bgColor: !isParent ? 'secondary-25' : undefined }}
        onClick={handleSelect}
        bgColor={isParent || isSelected ? 'secondary-25' : 'transparent'}
        borderBottom="1px solid"
        borderColor="border-color"
      >
        <KText
          fontSize="14px"
          color={
            isSelected
              ? 'primary-600'
              : isParent
                ? 'secondary-900'
                : 'secondary-700'
          }
          fontWeight={isParent ? 700 : 450}
          isTruncated
        >
          {item?.name}
        </KText>
        {isSelected && (
          <CheckCircle
            size={20}
            color="var(--kula-colors-primary-500)"
            className="kula-icon"
            style={{ flexShrink: 0 }}
          />
        )}
      </Box>
      {children?.map((child) => (
        <CandidateSourceTreeItem
          key={child.id}
          item={child}
          items={items}
          onClose={onClose}
          currentSelectedValue={currentSelectedValue}
          onSelect={onSelect}
        />
      ))}
    </>
  );
};

// Nested tree list for candidate source
const CandidateSourceNestedTreeList = ({
  data,
  onSelect,
  onClose,
  inputValue,
  currentSelectedValue,
}: {
  data: CandidateSource[];
  onSelect: (item: CandidateSource) => void;
  onClose?: () => void;
  inputValue?: string;
  currentSelectedValue?: CandidateSource | null;
}) => {
  // Filter items based on search input
  const filteredItems = useMemo(() => {
    if (!inputValue?.trim()) return [];
    const searchTerm = inputValue.toLowerCase().trim();
    return data.filter((item) => item.name.toLowerCase().includes(searchTerm));
  }, [inputValue, data]);

  // Filter root items to only show parents that have children
  const rootItems = useMemo(() => {
    return data?.filter((item) => {
      if (item?.parent_id) return false;
      return data?.some((child) => child?.parent_id === item?.id);
    });
  }, [data]);

  if (inputValue?.trim() && filteredItems.length === 0) {
    return (
      <Flex minH="2em" justifyContent="center" alignItems="center">
        <KText>No options found</KText>
      </Flex>
    );
  }

  return (
    <Box maxH="15em" overflowY="auto" p="0" w="100%">
      {inputValue?.trim()
        ? filteredItems.map((item) => (
            <CandidateSourceSearchResultItem
              key={item.id}
              item={item}
              items={data}
              onSelect={onSelect}
              onClose={onClose}
              isSelected={currentSelectedValue?.id === item?.id}
            />
          ))
        : rootItems?.map((item) => (
            <CandidateSourceTreeItem
              key={item?.id}
              item={item}
              items={data}
              onClose={onClose}
              currentSelectedValue={currentSelectedValue}
              onSelect={onSelect}
            />
          ))}
    </Box>
  );
};

/**
 * Custom Menu component that shows hierarchical options as a tree
 * Parent options are shown but not selectable
 * Only leaf nodes (items without children) can be selected
 */
export const CandidateSourceMenu = (props: any) => {
  const { options = [], selectProps = {}, innerRef, innerProps } = props;
  const {
    onChange,
    onMenuClose,
    onInputChange,
    inputValue = '',
    value,
  } = selectProps;

  const handleSelect = (selectedOption: CandidateSource) => {
    onChange(selectedOption);
    onInputChange?.('', { action: 'set-value' });
    onMenuClose?.();
  };

  return (
    <components.Menu {...props}>
      <Box ref={innerRef} {...innerProps}>
        <CandidateSourceNestedTreeList
          data={options}
          onSelect={handleSelect}
          onClose={onMenuClose}
          inputValue={inputValue}
          currentSelectedValue={value}
        />
      </Box>
    </components.Menu>
  );
};

/**
 * Helper function to filter options
 */
export const candidateSourceFilterOption = (
  option: { data: CandidateSource },
  inputValue: string
) => {
  const { data } = option;

  if (!inputValue) return true;

  return data.name.toLowerCase().includes(inputValue.toLowerCase());
};

/**
 * Get the components config for candidate source select
 */
export const getCandidateSourceComponents = () => ({
  Menu: CandidateSourceMenu,
  SingleValue: CandidateSourceSingleValue,
  IndicatorSeparator: () => null,
});
